import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../utils/supabase";
import Navbar from "../components/Navbar";

function AccountPage() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState("");
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    async function loadProfile() {
      // Get current user
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
        return;
      }
      setUser(session.user);

      // Fetch profile data
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();

      if (error) {
        console.error("Error fetching profile:", error);
      } else {
        setProfile(data);
      }
      setLoading(false);
    }

    loadProfile();
  }, [navigate]);

  const handlePasswordReset = async () => {
    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: window.location.origin + "/login",
    });
    if (error) {
      setMessage("Failed to send reset email. Try again.");
    } else {
      setMessage("Password reset email sent. Check your inbox.");
    }
  };

  const handleNameUpdate = async () => {
    const { error } = await supabase
      .from("profiles")
      .update({ full_name: newName })
      .eq("id", user.id);

    if (error) {
      setMessage("Failed to update name. Try again.");
    } else {
      setProfile({ ...profile, full_name: newName });
      setEditingName(false);
      setMessage("Name updated successfully.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#070707]">
        <Navbar />
        <div className="flex items-center justify-center pt-[120px]">
          <p className="text-[#888] text-[13px]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070707]">
      <Navbar />
      <div className="max-w-[600px] mx-auto px-6 pt-[120px] pb-20">
        {/* Page Title */}
        <h1
          className="text-text-primary text-[28px] mb-2"
          style={{ fontWeight: 700 }}
        >
          Account
        </h1>
        <p
          className="text-[#b0b0b0] text-[13px] mb-10"
          style={{ fontWeight: 500 }}
        >
          Manage your profile and subscription
        </p>

        {/* Profile section */}
        <section className="border border-[#222] p-6 mb-6">
          <h2
            className="text-[#888] text-[10px] uppercase tracking-[3px] mb-4"
            style={{ fontWeight: 600 }}
          >
            Profile
          </h2>
          <div className="flex items-center gap-4 mb-4">
            <div
              className="w-12 h-12 flex items-center justify-center text-[18px]"
              style={{
                backgroundColor: "#FF6B00",
                color: "#000",
                fontWeight: 700,
              }}
            >
              {(profile?.full_name || user?.email || "U")
                .charAt(0)
                .toUpperCase()}
            </div>
            <div>
              {editingName ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="bg-[#0a0a0a] border border-[#333] text-white text-[14px] px-3 py-1 outline-none focus:border-[#FF6B00]"
                    style={{ fontFamily: "Space Grotesk", fontWeight: 500 }}
                  />
                  <button
                    onClick={handleNameUpdate}
                    className="text-[10px] uppercase tracking-[2px] px-3 py-1"
                    style={{
                      backgroundColor: "#FF6B00",
                      color: "#000",
                      fontWeight: 700,
                    }}
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditingName(false)}
                    className="text-[10px] uppercase tracking-[2px] text-[#888] hover:text-[#b0b0b0]"
                    style={{ fontWeight: 600 }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <p
                    className="text-text-primary text-[14px]"
                    style={{ fontWeight: 600 }}
                  >
                    {profile?.full_name || "No name set"}
                  </p>
                  <button
                    onClick={() => {
                      setNewName(profile?.full_name || "");
                      setEditingName(true);
                    }}
                    className="text-[10px] text-[#888] hover:text-[#FF6B00] transition-colors"
                    style={{ fontWeight: 500 }}
                  >
                    Edit
                  </button>
                </div>
              )}
              <p className="text-[#888] text-[12px]">{user?.email}</p>
            </div>
          </div>
          <p className="text-[#888] text-[11px]">
            Member Since{" "}
            {new Date(profile?.created_at).toLocaleDateString("en-US", {
              month: "long",
              year: "numeric",
            })}
          </p>
        </section>

        {/* Subscription section */}
        <section className="border border-[#222] p-6 mb-6">
          <h2
            className="text-[#888] text-[10px] uppercase tracking-[3px] mb-4"
            style={{ fontWeight: 600 }}
          >
            Subscription
          </h2>
          <div className="flex items-center justify-between mb-4">
            <p
              className="text-text-primary text-[12px]"
              style={{ fontWeight: 600 }}
            >
              {profile?.subscription_tier === "pro" ? "Pro Plan" : "Free Plan"}
            </p>
            <p className="text-[#888] text-[12px] mt-1">
              {profile?.subscription_tier === "pro"
                ? "Your Pro plan is active"
                : "You are on the free plan"}
            </p>
          </div>
          {profile?.subscription_tier !== "pro" && (
            <button
              onClick={() => navigate("/pricing")}
              className="text-[10px] uppercase tracking-[3px] border px-4 py-2 mb-4 transition-all duration-200"
              style={{
                color: "#FF6B00",
                borderColor: "#FF6B00",
                backgroundColor: "transparent",
                fontWeight: 700,
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#FF6B00";
                e.target.style.color = "#000";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
                e.target.style.color = "#FF6B00";
              }}
            >
              Upgrade
            </button>
          )}
          <div className="border-t border-[#222] pt-4">
            <div className="flex justify-between text-[12px]">
              <span className="text-[#888]">Videos used this month</span>
              <span className="text-text-primary" style={{ fontWeight: 600 }}>
                {profile?.monthly_uploads_used || 0}
                {profile?.subscription_tier === "pro" ? "" : " / 2"}
              </span>
            </div>
          </div>
        </section>

        {/* Security section */}
        <section className="border border-[#222] p-6 mb-6">
          <h2
            className="text-[#888] text-[10px] uppercase tracking-[3px] mb-4"
            style={{ fontWeight: 600 }}
          >
            Security
          </h2>
          <button
            onClick={handlePasswordReset}
            className="text-[12px] text-[#b0b0b0] hover:text-[#FF6B00] transition-colors"
            style={{ fontWeight: 500 }}
          >
            Send Password Reset Email
          </button>
        </section>

        {/* Message */}
        {message && (
          <p
            className="text-[12px] mt-4"
            style={{
              fontWeight: 500,
              color: message.includes("sent") ? "#4ade80" : "#f87171",
            }}
          >
            {message}
          </p>
        )}
      </div>
    </div>
  );
}

export default AccountPage;
